/**
 * Horizontal Product Scroll Class
 * issue: https://rituals.atlassian.net/browse/DL-21863
 *
 * Adds horizontal product scroll functionality similar to the draglist class
 */
'use strict';

const gtm = require('./modules/google').tagManager;

const productScrollSelector = '.js-horizontal-product-scroll';
const scrollBodySelector = '.js-scroll-slides';
const scrollProgressSelector = '.js-scroll-progress';
const introSlide = '.js-intro';
const videoslide = '.js-video-slide';
const videoControls = '.js-responsive-video-controls';
const scrollElements = [];
const trackedDataLayerEvents = [];
const isinView = [];
let currentQuarterNumber = 0;
const scrollState = {
    'active': false,
    'rect': '',
    'startX': 0,
    'clientX': 0,
    'target': ''
};

const scrollObserver = new IntersectionObserver((scrollers) => {
    for (const scroller of scrollers) {
        const currentY = scroller.boundingClientRect.y;
        const previousY = parseInt(scroller.target.dataset.previousY) || 0;
        scroller.target.classList.toggle('intersecting', scroller.isIntersecting);

        if (scroller.isIntersecting) {
            scroller.target.querySelector(introSlide).classList.remove('scroll-bg-white');
        }

        scroller.target.querySelectorAll('.scroll-slide-col').forEach((el, i) => {
            if (scroller.isIntersecting && currentY < previousY) {
                setTimeout(() => {
                    el.style.opacity = '1';
                    el.style.transform = 'translate3d(0, 0, 0)';
                }, i * 150);
            }
        });

        scroller.target.dataset.previousY = currentY;
    }
}, {
    threshold: [0.33, 0.66]
});

/**
 * Prepares the elements and attaches all events to the product scroll
 */
const initScrollers = () => {
    document.querySelectorAll(productScrollSelector).forEach((el, i) => {
        const scrollBody = el.querySelector(scrollBodySelector);

        scrollBody.dataset.listIndex = i;
        scrollBody.querySelectorAll('a, img').forEach((draggableEl) => {
            draggableEl.setAttribute('draggable', 'false');
        });

        scrollBody.addEventListener('scroll', e => {
            setProductScrollProgress(e);
            scrollProgresToDataLayer(e);
            scrollPlayVideo(e);
        });

        scrollBody.addEventListener('mousedown', onMouseDown);
        scrollBody.addEventListener('mouseup', onMouseUp);
        scrollObserver.observe(el);
        scrollElements.push(scrollBody);
    });

    document.addEventListener('mousemove', onMouseMove);
};

/**
 * Sets the progress element based on scroll position
 * @param {Event} e ScrollEvent
 */
const setProductScrollProgress = (e) => {
    const scrollEl = e.currentTarget;
    const progressEl = e.currentTarget.parentNode.querySelector(scrollProgressSelector);
    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    const progressVal = (100 / maxScroll) * scrollEl.scrollLeft;

    progressEl.value = Math.max(2, progressVal);
};

const scrollPlayVideo = (e) => {
    const thisVideo = e.currentTarget.parentNode.querySelector(videoslide);
    const rect = thisVideo.getBoundingClientRect().x;
    const thisVideoControls = e.currentTarget.parentNode.querySelector(videoControls);
    let currentNum = 0;

    if (rect <= window.innerWidth) {
        currentNum = 1;
    }

    if (currentNum !== 0 && isinView.indexOf(currentNum) === -1) {
        if (rect <= window.innerWidth) {
            thisVideo.querySelector('video').play();
            thisVideoControls.classList.add('is-playing');
        }

        isinView.push(currentNum);
    }
};

const scrollProgresToDataLayer = (e) => {
    const $progressEl = $(e.currentTarget.parentNode.querySelector(scrollProgressSelector));
    const analyticsData = $progressEl.length > 0 && $progressEl.data('analytics');
    if (!analyticsData) {
        return;
    }

    const scrollEl = e.currentTarget;
    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    const progressVal = (100 / maxScroll) * scrollEl.scrollLeft;
    const rounded = Math.round(progressVal * 10) / 10;

    if (rounded >= 25 && rounded < 50) {
        currentQuarterNumber = 25;
    } else if (rounded >= 50 && rounded < 75) {
        currentQuarterNumber = 50;
    } else if (rounded >= 75 && rounded < 100) {
        currentQuarterNumber = 75;
    } else if (rounded == 100){
        currentQuarterNumber = 100;
    }

    if (currentQuarterNumber !== 0 && trackedDataLayerEvents.indexOf(currentQuarterNumber) === -1) {
        trackedDataLayerEvents.push(currentQuarterNumber);
        gtm.pushDataLayerEvent({
            ...analyticsData,
            // eslint-disable-next-line camelcase
            scroll_depth: currentQuarterNumber
        });
    }
};
/**
 * Resets all the variables to their initial value
 */
const onMouseUp = (e) => {
    const scrollEl = scrollElements[e.currentTarget.dataset.listIndex];

    scrollEl.classList.remove('grabbing');
    scrollState.target = '';
    scrollState.rect = '';
    scrollState.startX = 0;
    scrollState.clientX = 0;
    scrollState.active = false;
};

/**
 * Moves the product scroll
 * @param {Event} e Mouse move event
 */
const onMouseMove = (e) => {
    if (scrollState.active) {
        const scrollX = scrollState.startX - (e.clientX - scrollState.rect.x);
        const scrollEl = scrollElements[scrollState.target.dataset.listIndex];

        scrollEl.scrollLeft = scrollX;

        // Dispatch window scroll event so elementInView logic is triggered
        window.dispatchEvent(new CustomEvent('scroll'));
    }
};

/**
 * Sets all the variables needed to be able to move the product scroll
 * @param {Event} e Mouse down event
 */
const onMouseDown = (e) => {
    const scrollEl = scrollElements[e.currentTarget.dataset.listIndex];

    scrollEl.classList.add('grabbing');
    scrollState.target = e.currentTarget;
    scrollState.rect = scrollState.target.getBoundingClientRect();
    scrollState.startX = scrollEl.scrollLeft + e.clientX - scrollState.rect.x;
    scrollState.clientX = e.clientX;
    scrollState.active = true;
};

/**
 * Tests if the product scroll exists on the page
 * @returns {Boolean} result of test
 */
const shouldInit = () => {
    return document.querySelectorAll(productScrollSelector).length > 0;
};

module.exports = {
    init: () => {
        if (shouldInit()) {
            initScrollers();
        }
    }
};
